import React from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LoopIcon from '@mui/icons-material/Loop';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

export const getStatusBadge = (status) => {
  switch (status) {
    case 'open':
      return { label: 'Open', className: 'status-open', icon: <InfoOutlinedIcon fontSize="inherit" /> };
    case 'in_progress':
      return { label: 'In Progress', className: 'status-in_progress', icon: <LoopIcon fontSize="inherit" /> };
    case 'resolved':
      return { label: 'Resolved', className: 'status-resolved', icon: <CheckCircleOutlineIcon fontSize="inherit" /> };
    case 'closed':
      return { label: 'Closed', className: 'status-closed', icon: <HighlightOffIcon fontSize="inherit" /> };
    default:
      return { label: (status || 'open').replace('_', ' '), className: 'status-open', icon: <InfoOutlinedIcon fontSize="inherit" /> };
  }
};

export const getPriorityBadge = (priority) => {
  const p = (priority || 'medium').toLowerCase();
  switch (p) {
    case 'low':
      return { label: 'Low', className: 'priority-low' };
    case 'medium':
      return { label: 'Medium', className: 'priority-medium' };
    case 'high':
      return { label: 'High', className: 'priority-high' };
    case 'critical':
      return { label: 'Critical', className: 'priority-critical' };
    default:
      return { label: priority, className: 'priority-medium' };
  }
};

export const formatTimeAgo = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  return date.toLocaleDateString();
};

export const formatFullDate = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const parseAttachments = (attachments) => {
  if (!attachments) return [];

  let result = attachments;

  // 1. If it's a string, try parsing as JSON or fallback to raw string
  if (typeof result === 'string') {
    const trimmed = result.trim();
    if (!trimmed) return [];

    try {
      result = JSON.parse(trimmed);
    } catch (e) {
      if (trimmed.includes(',')) {
        return trimmed.split(',').map((s) => s.trim()).filter(Boolean);
      }
      return [trimmed];
    }
  }

  // 2. If result after parsing is still a string
  if (typeof result === 'string') {
    const trimmed = result.trim();
    if (!trimmed) return [];
    if (trimmed.includes(',')) {
      return trimmed.split(',').map((s) => s.trim()).filter(Boolean);
    }
    return [trimmed];
  }

  // 3. If result is an array
  if (Array.isArray(result)) {
    const flat = [];
    result.forEach((item) => {
      if (!item) return;
      if (typeof item === 'string' || typeof item === 'object') {
        const parsedItems = parseAttachments(item);
        flat.push(...parsedItems);
      }
    });
    return flat;
  }

  // 4. If result is a single object
  if (typeof result === 'object' && result !== null) {
    return [result];
  }

  return [];
};

/**
 * Resolves full viewable/downloadable URL for any attachment format
 */
export const getFileUrl = (att) => {
  if (!att) return '';

  let rawUrl = '';

  if (typeof att === 'string') {
    rawUrl = att;
  } else if (typeof att === 'object' && att !== null) {
    rawUrl =
      att.fileUrl ||
      att.file_url ||
      att.url ||
      att.filename ||
      att.fileName ||
      att.file_name ||
      att.path ||
      att.file_path ||
      att.location ||
      att.link ||
      att.src ||
      att.uri ||
      '';
  }

  if (!rawUrl || typeof rawUrl !== 'string') return '';
  const trimmedUrl = rawUrl.trim();
  if (!trimmedUrl) return '';

  // Return as-is if already an absolute URL or data/blob URI
  if (
    trimmedUrl.startsWith('http://') ||
    trimmedUrl.startsWith('https://') ||
    trimmedUrl.startsWith('blob:') ||
    trimmedUrl.startsWith('data:')
  ) {
    return trimmedUrl;
  }

  const baseRoute = (process.env.REACT_APP_API_ROUTE || '').replace(/\/+$/, '');

  // Clean leading slashes and redundant 'files/' prefix
  let cleanPath = trimmedUrl.replace(/^\/+/, '');
  if (cleanPath.startsWith('files/')) {
    cleanPath = cleanPath.replace(/^files\//, '');
  }

  if (!baseRoute) return `/files/${cleanPath}`;

  return `${baseRoute}/files/${cleanPath}`;
};

/**
 * Extract clean, human-readable file name for attachment chips
 */
export const getFileName = (att, index = 0) => {
  if (!att) return `Attachment ${index + 1}`;

  if (typeof att === 'string') {
    const trimmed = att.trim();
    const parts = trimmed.split(/[\/\\]/);
    const lastPart = parts[parts.length - 1];
    if (lastPart) return lastPart;
  }

  if (typeof att === 'object' && att !== null) {
    const name =
      att.fileName ||
      att.filename ||
      att.file_name ||
      att.name ||
      att.originalName ||
      att.originalname ||
      att.original_name ||
      att.title ||
      att.label ||
      '';

    if (name) return name;

    const url = getFileUrl(att);
    if (url) {
      const parts = url.split(/[\/\\]/);
      const lastPart = parts[parts.length - 1];
      if (lastPart) return lastPart;
    }
  }

  return `Attachment ${index + 1}`;
};
